import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search,
  Camera,
  Check,
  RefreshCw,
  Image,
  Film,
  Heart,
} from "lucide-react";
import { OpenRouterModel } from "../services/openRouterModels";
import {
  BrowserCategory,
  getOrderedBrowserCategories,
  isFreeModel,
  hasVision,
  hasImageGeneration,
  hasVideoGeneration,
  getProvider,
  cleanModelName,
  formatPrice,
  formatContext,
  filterByCategory,
  searchModels,
  sortModels,
  SortOption,
} from "../utils/modelCategorizer";
import { useAuth } from "../contexts/AuthContext";
import { loadFavorites, saveFavorites } from "../firebase/favorites";

interface ModelBrowserProps {
  models: OpenRouterModel[];
  loading: boolean;
  selectedModel: string;
  onSelectModel: (id: string) => void;
  /** Hide the heading (used when wrapped in a modal with its own header) */
  showHeader?: boolean;
  /** Called when user clicks retry after an error */
  onRetry?: () => void;
  error?: string | null;
}

const PAGE_SIZE = 12;
const orderedCategories = getOrderedBrowserCategories();

const ModelBrowser: React.FC<ModelBrowserProps> = ({
  models,
  loading,
  selectedModel,
  onSelectModel,
  showHeader = true,
  onRetry,
  error,
}) => {
  const { currentUser } = useAuth();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<BrowserCategory>(currentUser ? "All" : "Free");
  const [sort, setSort] = useState<SortOption>("recommended");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [expandedDesc, setExpandedDesc] = useState<Set<string>>(
    () => new Set(),
  );
  const [favorites, setFavorites] = useState<string[]>([]);
  const [favoriteError, setFavoriteError] = useState<string | null>(null);
  const [lastToggledFavorite, setLastToggledFavorite] = useState<string | null>(null);

  useEffect(() => {
    if (models.length > 0 && activeCategory === "Free" && !models.some(isFreeModel)) setActiveCategory("All");
  }, [models, activeCategory]);

  useEffect(() => {
    let active = true;
    loadFavorites(currentUser?.uid ?? null).then((ids) => { if (active) setFavorites(ids); }).catch(() => {});
    return () => { active = false; };
  }, [currentUser?.uid]);

  const toggleFavorite = useCallback(
    (modelId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const previous = favorites;
      const next = favorites.includes(modelId)
        ? favorites.filter((id) => id !== modelId)
        : [...favorites, modelId];
      setFavorites(next);
      setLastToggledFavorite(modelId);
      setFavoriteError(null);
      Promise.resolve(saveFavorites(currentUser?.uid ?? null, next)).catch((err) => {
        console.error("Failed to save favorites:", err);
        setFavorites(previous);
        setFavoriteError("Couldn’t save that favorite. Try again.");
      });
    },
    [favorites, currentUser?.uid],
  );


  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<BrowserCategory, number>> = {};
    for (const cat of orderedCategories) {
      if (cat === "Favorites") {
        counts[cat] = favorites.filter((id) =>
          models.some((m) => m.id === id),
        ).length;
      } else {
        counts[cat] = filterByCategory(models, cat).length;
      }
    }
    return counts;
  }, [models, favorites]);

  // Filtered & sorted models
  const filteredModels = useMemo(() => {
    let result: OpenRouterModel[];
    if (activeCategory === "Favorites") {
      result = models.filter((m) => favorites.includes(m.id));
    } else {
      result = filterByCategory(models, activeCategory);
    }
    result = searchModels(result, search);
    result = sortModels(result, sort);
    return result;
  }, [models, activeCategory, search, sort, favorites]);

  const visibleModels = filteredModels.slice(0, visibleCount);
  const remaining = filteredModels.length - visibleCount;

  const handleCategoryChange = (cat: BrowserCategory) => {
    setActiveCategory(cat);
    setVisibleCount(PAGE_SIZE);
  };

  const toggleDesc = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDesc((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="model-browser">
      {showHeader && (
        <div className="model-browser-heading">
          <h2 className="text-xl font-semibold text-content mb-1">
            Choose a model
          </h2>
          <p className="text-sm text-muted">
            {models.length > 0
              ? `Browse ${models.length} AI models. Pick one for this conversation.`
              : "Loading available models…"}
          </p>
        </div>
      )}

      {favoriteError && (
        <p role="alert" className="motion-fade mb-3 rounded-md border border-danger-line bg-danger-soft px-3 py-2 text-xs text-danger">
          {favoriteError}
        </p>
      )}

      <div className="relative mb-3">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
        />
        <input
          type="text"
          aria-label="Search models"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(PAGE_SIZE);
          }}
          placeholder="Search models, providers…"
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-line rounded-xl bg-surface focus:outline-none focus:ring-2 focus:ring-accent-line/20 focus:border-accent-line text-content"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear model search"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-muted"
          >
            ✕
          </button>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scrollbar-hide">
        {orderedCategories.map((cat) => {
          const count = categoryCounts[cat] ?? 0;
          // Always show Favorites chip so users discover the feature
          if (cat !== "Favorites" && count === 0) return null;
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              aria-pressed={isActive}
              onClick={() => handleCategoryChange(cat)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors duration-150 focus-visible:outline-offset-2 ${
                isActive
                  ? "bg-action text-on-solid border-accent-line shadow-xs"
                  : "bg-surface text-muted border-line hover:bg-subtle hover:border-control active:bg-raised"
              }`}
            >
              <span className="whitespace-nowrap">{cat}</span>
              <span
                className={`tabular-nums ${isActive ? "text-on-solid" : "text-muted"}`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3 px-0.5">
        <span className="text-xs text-muted tabular-nums">
          {filteredModels.length} model
          {filteredModels.length !== 1 ? "s" : ""}
        </span>
        <label className="flex items-center gap-2 text-xs text-muted">Sort
          <select aria-label="Sort models" className="rounded-lg border border-line bg-surface px-2 py-2 text-xs text-content" value={sort} onChange={e => setSort(e.target.value as SortOption)}>
            <option value="recommended">Recommended</option><option value="price-asc">Price: Low to high</option><option value="price-desc">Price: High to low</option><option value="context-desc">Context: Largest</option><option value="name-asc">Name: A to Z</option>
          </select>
        </label>
      </div>

      {loading && models.length === 0 && (
        <div className="model-card-grid" role="status" aria-label="Loading models">
          {[62, 48, 70, 55, 66, 44].map((w, i) => (
            <div key={i} className="model-card" aria-hidden="true">
              <div className="flex items-center gap-2 mb-2.5 pr-8">
                <span className="skeleton h-4" style={{ width: `${w}%` }} />
                <span className="skeleton h-4 w-9" />
              </div>
              <span className="skeleton block h-3 w-3/5 mb-3" />
              <span className="skeleton block h-2.5 w-full mb-1.5" />
              <span className="skeleton block h-2.5" style={{ width: `${w + 18}%` }} />
            </div>
          ))}
        </div>
      )}

      {error && models.length === 0 && (
        <div className="text-center py-10">
          <p className="text-sm text-danger mb-3">
            Failed to load models: {error}
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-accent bg-accent-soft hover:bg-accent-soft border border-accent-line rounded-xl transition-colors"
            >
              <RefreshCw size={14} />
              Retry
            </button>
          )}
        </div>
      )}

      {models.length > 0 && (
        <div className="model-card-grid motion-fade">
          {activeCategory === "Favorites" && filteredModels.length === 0 && (
            <div className="text-center py-12">
              <Heart size={36} className="mx-auto mb-3 text-muted" />
              <p className="text-sm font-medium text-muted mb-1">
                No favorites yet
              </p>
              <p className="text-xs text-muted">
                Select the heart on any model to keep it here
              </p>
            </div>
          )}
          {visibleModels.map((m) => {
            const isSelected = m.id === selectedModel;
            const provider = getProvider(m.id);
            const name = cleanModelName(m.name);
            const free = isFreeModel(m);
            const vision = hasVision(m);
            const imageGeneration = hasImageGeneration(m);
            const videoGeneration = hasVideoGeneration(m);
            const isFav = favorites.includes(m.id);
            const desc = m.description || "";
            const isLong = desc.length > 120;
            const descExpanded = expandedDesc.has(m.id);
            const shortDesc = isLong ? desc.substring(0, 120) + "…" : desc;

            return (
              <article key={m.id} className={`model-card ${isSelected ? "is-selected" : ""}`}>
                <button type="button" aria-label={`${isFav ? "Remove" : "Add"} ${name} ${isFav ? "from" : "to"} favorites`} aria-pressed={isFav}
                  onClick={e => toggleFavorite(m.id, e)} className="favorite-button">
                  <Heart key={isFav ? "on" : "off"} size={16} aria-hidden="true" className={`transition-colors duration-150 ${isFav ? `fill-current text-favorite ${lastToggledFavorite === m.id ? "motion-heart" : ""}` : "text-muted"}`} />
                </button>
                <button type="button" className="model-card-select" onClick={() => onSelectModel(m.id)} aria-pressed={isSelected} aria-label={`Select ${name}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm font-semibold text-content truncate flex-1">
                    {name}
                  </span>
                  {free && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold bg-success-soft text-success rounded-md flex-shrink-0">
                      FREE
                    </span>
                  )}
                  {vision && (
                    <Camera size={12} className="text-accent flex-shrink-0" />
                  )}
                  {imageGeneration && (
                    <Image
                      size={12}
                      className="text-success flex-shrink-0"
                    />
                  )}
                  {videoGeneration && (
                    <Film size={12} className="text-favorite flex-shrink-0" />
                  )}
                  {isSelected && (
                    <Check size={14} className="text-accent flex-shrink-0" />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted mb-1 tabular-nums">
                  <span className="font-medium text-muted">{provider}</span>
                  <span aria-hidden="true" className="text-muted">·</span>
                  <span>
                    {formatPrice(m.pricing.prompt)} in /{" "}
                    {formatPrice(m.pricing.completion)} out
                  </span>
                  {m.context_length > 0 && (
                    <>
                      <span aria-hidden="true" className="text-muted">·</span>
                      <span>{formatContext(m.context_length)} ctx</span>
                    </>
                  )}
                </div>

                </button>
                {desc && <p className="model-card-description">{descExpanded ? desc : shortDesc}</p>}
                {isLong && <button type="button" className="model-description-toggle" aria-expanded={descExpanded} aria-label={`${descExpanded ? "Less" : "More"} about ${name}`} onClick={e => toggleDesc(m.id, e)}>{descExpanded ? "Read less" : "Read more"}</button>}
              </article>
            );
          })}

          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
              className="w-full py-3 text-sm font-semibold text-accent bg-accent-soft hover:bg-accent-soft border border-accent-line rounded-xl transition-colors"
            >
              Load {Math.min(PAGE_SIZE, remaining)} more
              <span className="text-accent font-normal ml-1">
                ({remaining} remaining)
              </span>
            </button>
          )}

          {filteredModels.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-muted mb-1">
                No models match your search.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setActiveCategory("All");
                }}
                className="text-xs text-accent hover:text-accent font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelBrowser;
